import { Component, inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DiaryFormService } from '../../../../core/services/diary-form.service';
import { requiredNoWhitespaceValidator } from '../../../../core/validators/no-whitespace.validator';
import { AlertService } from '../../../../shared/alert/alert.service';
import { DiaryFormSummary } from '../../../../core/models/trial-diary-form/diary-form-summary.model';
import { CreateDiaryFormRequest } from '../../../../core/models/trial-diary-form/create-diary-form-request.model';
import { ModalComponent } from '../../../../shared/modal/modal.component';
import { LogHelper } from '../../../../core/helpers/log.helper';
import { DiaryType } from '../../../../core/constants/diary-form-type.enum';
import { DropdownInputComponent } from '../../../../shared/dropdown-input/dropdown-input.component';
import { DateHelper } from '../../../../core/helpers/date-helper';

@Component({
  selector: 'app-trial-diaries',
  templateUrl: './trial-diaries.component.html',
  styleUrl: './trial-diaries.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class TrialDiariesComponent implements OnInit {
  @Input() trialId: string;
  @ViewChild('addDiaryModal') addDiaryModal: ModalComponent;
  @ViewChild('diaryTypeDropdown') diaryTypeDropdown: DropdownInputComponent;

  private readonly _diaryFormService = inject(DiaryFormService);
  private readonly _alertService = inject(AlertService);

  protected diaries: DiaryFormSummary[] = [];
  protected selectedDiaryId: string | null = null;
  protected isLoading = false;
  protected isCreating = false;

  protected readonly DiaryType = DiaryType;
  protected addDiaryDefaultIntraDayReminderTimes: number[] = [];

  protected readonly diaryTypeOptions = [
    { value: DiaryType.Periodic.toString(), text: 'Periodic' },
    { value: DiaryType.OnboardingQuestions.toString(), text: 'Onboarding Questions' },
    { value: DiaryType.IntraDay.toString(), text: 'Intra Day' },
  ];

  protected addDiaryForm = new FormGroup({
    name: new FormControl<string>('', [requiredNoWhitespaceValidator(), Validators.maxLength(100)]),
    diaryType: new FormControl<string>(DiaryType.OnboardingQuestions.toString(), [Validators.required]),
    defaultReminderTime: new FormControl<string>('08:00'),
    newIntraDayReminderTime: new FormControl<string>('')
  });

  ngOnInit() {
    this.loadDiaries();
  }

  loadDiaries() {
    this.isLoading = true;
    this._diaryFormService.list(this.trialId).subscribe({
      next: diaries => {
        this.diaries = diaries;
        if (diaries.length > 0 && !this.selectedDiaryId) {
          this.selectedDiaryId = diaries[0].id;
        }
        this.isLoading = false;
      },
      error: err => {
        LogHelper.log(err);
        this.isLoading = false;
        this._alertService.showWarningAlert('There was an error loading diaries.');
      }
    });
  }

  selectDiary(id: string) {
    this.selectedDiaryId = id;
  }

  getDiaryDisplayName(diary: DiaryFormSummary): string {
    return diary.name?.trim() || 'Default Diary';
  }

  getDiaryTypeIcon(diary: DiaryFormSummary): string {
    switch (diary.diaryType) {
      case DiaryType.OnboardingQuestions: return 'far fa-clipboard-list';
      case DiaryType.IntraDay: return 'far fa-book';
      default: return 'far fa-calendar-alt';
    }
  }

  onAddDiaryClick() {
    this.diaryTypeDropdown.reset();
    this.addDiaryForm.reset({ name: '', diaryType: null, defaultReminderTime: '08:00', newIntraDayReminderTime: '' });
    this.addDiaryDefaultIntraDayReminderTimes = [];
    this.addDiaryModal.show();
  }

  addIntraDayReminderTime(): void
  {
    const timeValue = this.addDiaryForm.get('newIntraDayReminderTime').value;

    if (!timeValue)
    {
      return;
    }

    const minutes = DateHelper.convert24HourTimeToMinutes(timeValue);

    if (this.addDiaryDefaultIntraDayReminderTimes.includes(minutes))
    {
      return;
    }

    this.addDiaryDefaultIntraDayReminderTimes = [...this.addDiaryDefaultIntraDayReminderTimes, minutes].sort((a, b) => a - b);
    this.addDiaryForm.get('newIntraDayReminderTime').setValue('');
  }

  removeIntraDayReminderTime(minutes: number): void
  {
    this.addDiaryDefaultIntraDayReminderTimes = this.addDiaryDefaultIntraDayReminderTimes.filter(t => t !== minutes);
  }

  formatReminderTime(minutes: number): string
  {
    return DateHelper.convertMinutesTo24HourTime(minutes);
  }

  onDiaryRenamed(name: string) {
    const diary = this.diaries.find(d => d.id === this.selectedDiaryId);
    if (diary) {
      diary.name = name;
    }
  }

  onDiaryDeleted(deletedId: string) {
    this.diaries = this.diaries.filter(d => d.id !== deletedId);
    if (this.selectedDiaryId === deletedId) {
      this.selectedDiaryId = this.diaries.length > 0 ? this.diaries[0].id : null;
    }
    this._alertService.showSuccessAlert('Diary deleted successfully.');
  }

  onConfirmAddDiary() {
    if (this.addDiaryForm.invalid)
    {
      return;
    }

    this.isCreating = true;

    const request: CreateDiaryFormRequest = {
      name: this.addDiaryForm.get('name').value.trim(),
      diaryType: Number(this.addDiaryForm.get('diaryType').value) as DiaryType,
      defaultReminderTimeMinutes: DateHelper.convert24HourTimeToMinutes(this.addDiaryForm.get('defaultReminderTime').value),
      defaultIntraDayReminderTimes: this.addDiaryDefaultIntraDayReminderTimes
    };

    this._diaryFormService.create(this.trialId, request).subscribe({
      next: newDiary => {
        this.diaries = [...this.diaries, newDiary];
        this.selectedDiaryId = newDiary.id;
        this.isCreating = false;
        this.addDiaryModal.hide();
        this._alertService.showSuccessAlert('Diary created successfully.');
      },
      error: err => {
        LogHelper.log(err);
        this.isCreating = false;
        this._alertService.showErrorAlert(err);
      }
    });
  }
}
